import defaultImage from 'assets/images/placeholder/default.png';
import { BASEURL } from './Constants';
import { Bill, BillSummary, MenuItem, MenuItemResponse, MenuType, RawMenuItemResponse } from '../types';
import { format, parse } from 'date-fns';
import { FormattedMessage } from 'react-intl';
import { MENU_ICONS } from '../menu-items/Icons';
import React from 'react';
import { Typography } from '@mui/material';

export const fullFilePath = (picPath: string) => {
    if (!picPath) {
        return defaultImage;
    }
    if (picPath.includes('http')) {
        return picPath;
    }
    return `${BASEURL}${picPath
        ?.split('/')
        .filter((v) => v !== '')
        .join('/')}`;
};

export const checkNullInfo = (info: string | null | undefined) => {
    if (info) {
        return info;
    }
    return '---';
};

export const getFieldLabel = () => {};

export const getToken = (storageName: string) => {
    let token = null;
    const rememberMe = JSON.parse(localStorage.getItem('rememberMe') as any);

    if (rememberMe) {
        token = window.localStorage.getItem(storageName);
    } else {
        token = window.sessionStorage.getItem(storageName);
    }
    return token;
};

export const setToken = (storageName: string, value: string) => {
    let token = null;
    const rememberMe = JSON.parse(localStorage.getItem('rememberMe') as any);

    if (rememberMe) {
        token = window.localStorage.setItem(storageName, value);
    } else {
        token = window.sessionStorage.setItem(storageName, value);
    }
    return token;
};

export const errMsg = (ex: any) => {
    if (ex.response.data.errors) {
        return ex.response.data.errors.map((e: any) => e.detail).join(', ');
    }
    return ex.response.data;
};

export const getDayNumberFromLabel = (dayLabel: string) => {
    switch (dayLabel) {
        case 'Mon':
            return 1;
        case 'Tue':
            return 2;
        case 'Wed':
            return 3;
        case 'Thu':
            return 4;
        case 'Fri':
            return 5;
        case 'Sat':
            return 6;
        case 'Sun':
            return 7;
        default:
            return -1; // Handle unknown labels gracefully if needed
    }
};

export const getLabelFromDayNumber = (dayNumber: number) => {
    switch (dayNumber) {
        case 1:
            return 'Monday';
        case 2:
            return 'Tuesday';
        case 3:
            return 'Wednesday';
        case 4:
            return 'Thursday';
        case 5:
            return 'Friday';
        case 6:
            return 'Saturday';
        case 7:
            return 'Sunday';
        default:
            return 'Invalid Day Number'; // Handle unknown numbers gracefully if needed
    }
};

export function processRawMenuItem(rawResponse: RawMenuItemResponse): MenuItemResponse {
    const { title, url, icon, type, breadcrumbs, caption, children } = rawResponse;

    const processedMenuItemResponse: MenuItemResponse = {
        id: title,
        title,
        type: type as MenuType
    };

    processedMenuItemResponse.breadcrumbs = breadcrumbs;

    if (icon) processedMenuItemResponse.icon = icon;
    if (url) processedMenuItemResponse.url = url;
    // if (breadcrumbs) processedMenuItemResponse.breadcrumbs = breadcrumbs;
    if (caption) processedMenuItemResponse.caption = caption;

    if (children && children.length > 0) {
        processedMenuItemResponse.children = children.map(processRawMenuItem);
    }

    return processedMenuItemResponse;
}

export const from24To12 = (time: string) => {
    const parsedTime = parse(time, 'HH:mm', new Date());

    return format(parsedTime, 'h:mm a');
};

export function generateTransactionId(transactionType: string) {
    const date = new Date();
    const year = date.getFullYear().toString().slice(-2);
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const day = ('0' + date.getDate()).slice(-2);
    const hour = ('0' + date.getHours()).slice(-2);
    const minute = ('0' + date.getMinutes()).slice(-2);
    const second = ('0' + date.getSeconds()).slice(-2);

    // Concatenate transaction ID components
    const transactionId = `TRN${year}${month}${day}${hour}${minute}${second}${transactionType}`;

    return transactionId;
}

export function billCalculation(bills: Bill[]): BillSummary {
    const totalDue = bills.reduce((previousValue, currentValue) => {
        return previousValue + currentValue.dueAmount;
    }, 0);
    const totalDiscount = bills.reduce((previousValue, currentValue) => {
        return previousValue + currentValue.discount;
    }, 0);
    const totalAdvance = bills.reduce((previousValue, currentValue) => {
        const advance = currentValue.advance ?? 0;
        return previousValue + advance;
    }, 0);

    return {
        totalDue,
        totalDiscount,
        totalAdvance
    };
}

export function formatMenu(menu: MenuItemResponse) {
    const formattedMenu: MenuItem = {
        id: menu.id,
        title: <FormattedMessage id={menu.title} />,
        type: menu.type,
        breadcrumbs: menu.breadcrumbs
    };

    if (menu.icon) {
        formattedMenu.icon = MENU_ICONS[menu.icon];
    }

    if (menu.url) {
        formattedMenu.url = menu.url;
    }

    if (menu.children) {
        formattedMenu.children = menu.children.map(formatMenu);
    }

    return { ...formattedMenu };
}

export function numberSeparateWithCommas(numberOrString: string | number | undefined): string {
    let number;
    if (typeof numberOrString === 'string') {
        number = parseFloat(numberOrString);
        if (isNaN(number)) {
            // If parsing fails, return the original input
            return numberOrString;
        }
    } else if (typeof numberOrString === 'undefined') {
        number = 0;
    } else {
        number = numberOrString;
    }

    let numberStr = number.toFixed(2); // Ensure two decimal places

    // Check if the number is negative
    let isNegative = false;
    if (numberStr.startsWith('-')) {
        isNegative = true;
        numberStr = numberStr.substring(1); // Remove the negative sign for now
    }

    // Check if the number starts with a decimal point
    let hasDecimalPoint = false;
    if (numberStr.startsWith('.')) {
        hasDecimalPoint = true;
        numberStr = '0' + numberStr; // Add a leading zero for now
    }

    let parts = numberStr.split('.');
    let integerPart = parts[0];
    let decimalPart = parts.length > 1 ? '.' + parts[1] : '';

    // Add .00 to the decimal part if it's absent
    if (decimalPart === '') {
        decimalPart = '.00';
    }

    // Do not add commas if the integer part is empty (happens with negative numbers or numbers starting with a decimal point)
    let integerPartWithCommas =
        integerPart === ''
            ? integerPart
            : integerPart
                  .split('')
                  .reverse()
                  .reduce((acc, digit, i) => {
                      if (i > 0 && i % 3 === 0) {
                          acc = ',' + acc;
                      }
                      acc = digit + acc;
                      return acc;
                  }, '');

    // Add back the leading zero if necessary
    if (hasDecimalPoint) {
        integerPartWithCommas = integerPartWithCommas.substring(1);
    }

    // Wrap negative numbers in brackets and include the decimal part inside the brackets
    if (isNegative) {
        return `(-${integerPartWithCommas}${decimalPart})`;
    }

    return integerPartWithCommas + decimalPart;
}

export function splitAddress(address: string | undefined) {
    if (!address) return ''; // Handle empty or undefined address

    if (address.length > 30) {
        // If the length is greater than 30, find the next space after 50 characters
        const index = address.indexOf(' ', 30);
        if (index !== -1) {
            return `${address.substring(0, index)}\n${address.substring(index + 1)}`;
        }
    } else {
        // If the length is less than or equal to 30, find the last space
        const lastSpaceIndex = address.lastIndexOf(' ');
        if (lastSpaceIndex !== -1) {
            return `${address.substring(0, lastSpaceIndex)}\n${address.substring(lastSpaceIndex + 1)}`;
        }
    }

    // If no space found, return original address
    return address;
}

export function searchDateFormatter(timeStart: string, timeEnd: string): string {
    const parsedDateStart = parse(timeStart, 'yyyy-MM-dd h:mm:ss a', new Date());
    const parsedDateEnd = parse(timeEnd, 'yyyy-MM-dd h:mm:ss a', new Date());
    // const formattedDateStart = format(parsedDateStart, 'dd-MMM, yyyy, EEEE h:mm a');
    const datePartStart = format(parsedDateStart, 'dd-MMM, yyyy');
    const timePartStart = format(parsedDateStart, 'EEEE h:mm a');

    // const formattedDateEnd = format(parsedDateEnd, 'dd-MMM, yyyy, EEEE h:mm a');
    const datePartEnd = format(parsedDateEnd, 'dd-MMM, yyyy');
    const timePartEnd = format(parsedDateEnd, 'EEEE h:mm a');
    const mergeTime = `${datePartStart} ${timePartStart} ${datePartEnd} ${timePartEnd}`;
    return mergeTime;
}

export function listFormatDate(dateToFormat: string, showTimePart: boolean) {
    const parsedDate = parse(dateToFormat, 'yyyy-MM-dd h:mm:ss a', new Date());

    if (isNaN(parsedDate.getTime())) {
        // Handle invalid date
        return <Typography variant={'subtitle1'}>{dateToFormat}</Typography>;
    }
    // const formattedDate = format(parsedDate, 'dd-MMM, yyyy, EEEE h:mm a');
    const datePart = format(parsedDate, 'dd-MMM, yyyy');
    const timePart = format(parsedDate, 'EEEE h:mm a');
    return (
        <>
            <Typography variant={'subtitle1'}>{datePart}</Typography>
            {showTimePart && <Typography variant={'subtitle2'}>{timePart}</Typography>}
        </>
    );
}

export function listFormatTime(dateToFormat: string) {
    const parsedDate = parse(dateToFormat, 'yyyy-MM-dd h:mm:ss a', new Date());

    if (isNaN(parsedDate.getTime())) {
        // Handle invalid date
        return <Typography variant={'subtitle1'}>{dateToFormat}</Typography>;
    }
    // const formattedDate = format(parsedDate, 'dd-MMM, yyyy, EEEE h:mm a');
    // const datePart = format(parsedDate, 'dd-MMM, yyyy');
    const timePart = format(parsedDate, 'h:mm a');
    return (
        <>
            <Typography variant={'subtitle1'}>{timePart}</Typography>
        </>
    );
}
